import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createTenantStorage } from './tenantStorage';
import { FORCE_UPDATE_AFTER_REMIND_COUNT } from './autoUpdaterPreferences.logic';

type AutoUpdaterPreferencesState = {
  snoozedUntil: number | null;
  skippedVersion: string | null;
  count: Record<string, number>;
  dialogOpenedByUser: boolean;
  setSnoozedUntil: ({ snoozedUntil }: { snoozedUntil: number | null }) => void;
  setSkippedVersion: ({
    skippedVersion,
  }: {
    skippedVersion: string | null;
  }) => void;
  incrementRemindLater: ({ version }: { version: string }) => void;
  clearVersionPreferences: ({ version }: { version: string }) => void;
  setDialogOpenedByUser: ({ openedByUser }: { openedByUser: boolean }) => void;
};

const STORAGE_KEY = 'terra-proxy-auto-updater-state';

const initialState = {
  snoozedUntil: null as number | null,
  skippedVersion: null as string | null,
  count: {} as Record<string, number>,
  dialogOpenedByUser: false,
};

export const useAutoUpdaterPreferencesStore =
  create<AutoUpdaterPreferencesState>()(
    persist(
      (set, get) => ({
        ...initialState,
        setSnoozedUntil: ({ snoozedUntil }) => set({ snoozedUntil }),
        setSkippedVersion: ({ skippedVersion }) => set({ skippedVersion }),
        incrementRemindLater: ({ version }) => {
          const currentCount = get().count[version] ?? 0;
          set({
            count: { ...get().count, [version]: currentCount + 1 },
          });
        },
        setDialogOpenedByUser: ({ openedByUser }) =>
          set({ dialogOpenedByUser: openedByUser }),
        clearVersionPreferences: ({ version }) => {
          const nextCounts = { ...get().count };
          delete nextCounts[version];
          set({
            count: nextCounts,
            skippedVersion:
              get().skippedVersion === version ? null : get().skippedVersion,
            snoozedUntil: null,
          });
        },
      }),
      {
        name: STORAGE_KEY,
        storage: createJSONStorage(() => createTenantStorage()),
        partialize: (state) => ({
          snoozedUntil: state.snoozedUntil,
          skippedVersion: state.skippedVersion,
          count: state.count,
        }),
      },
    ),
  );

export { FORCE_UPDATE_AFTER_REMIND_COUNT };
